import React, { Component } from 'react';

import PropTypes from 'prop-types';
import ms from 'ms';
import { Redirect } from 'react-router-dom';

import { USER_ROLE_RENTER, hasRoleLandlord } from 'common/userRoles';

import { isSunroomListing } from 'common/listingHelpers';

import { SCREEN_ID_RENTER_PROFILE } from 'client/constants/screenIds';
import makeInviteRoommateLink from 'common/util/makeInviteRoommateLink';
import withTransitionHook from 'client/helpers/withTransitionHook';
import allowedRoles from 'client/helpers/allowedRoles';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withLocationQuery from 'client/helpers/withLocationQuery';
import branch from 'client/core/branch';

import HouseholdActions from 'client/actions/Household';
import HouseholdApplicationActions from 'client/actions/HouseholdApplication';
import UserActions from 'client/actions/User';

import SrLink from 'client/components/SrLink/SrLink';
import Navbar from 'client/components/Navbar';
import TabBar from 'client/components/TabBar';
import SrCompleteYourProfileBlock from 'client/components/SrCompleteYourProfileBlock';
import SrRenterApplicationStatusCard from 'client/components/SrRenterApplicationStatusCard';
import SrRenterApplicationStatusCardSunroom from 'client/components/SrRenterApplicationStatusCardSunroom';
import SrSettingsModal from 'client/components/SrSettingsModal';
import SrInviteRoommateModal from 'client/components/SrInviteRoommateModal';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrIcon from 'client/components/SrIcon';
import SrApplicationProcessVideo from 'client/components/SrApplicationProcessVideo';

const getAppWithUtilityActivation = (householdApplications = []) => {
  return householdApplications.find((app) => {
    return app.utilityActivation && !app.utilityActivation.archivedAt;
  });
};

@withTransitionHook
@allowedRoles({
  roles: [USER_ROLE_RENTER],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_RENTER_PROFILE)
@branch({
  currentUser: ['currentUser'],
  appUrl: ['env', 'APP_URL'],
})
@branch(({ currentUser }) => ({
  userHouseholdApplications: ['household', 'applicationsByUserId', currentUser.id],
}))
@withScrollToTop
@withLocationQuery
class RenterProfilePage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Profile | Sunroom Rentals',
    keywords: 'sunroom, profile',
  });

  static fetchData(match, inject) {
    const userId = inject.state.get(['currentUser', 'id']);
    if (userId) {
      return Promise.all([
        HouseholdActions.loadRenterProfile(inject, { userId }),
        HouseholdApplicationActions.getHouseholdApplicationsByUserId(inject, { userId }),
        UserActions.getCustomerSupportPhoneNumber(inject, { userId }),
      ]);
    }
  }

  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    userHouseholdApplications: PropTypes.array,
    appUrl: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      showSettingsModal: false,
      showInviteRoommateModal: false,
      roommateInviteLink: null,
    };
  }

  componentDidMount() {
    const { viewingListingId } = this.props.locationQuery;

    this.refreshHouseholdApplicationsInterval = global.setInterval(
      this._refreshHouseholdApplications,
      ms('60s'),
    );

    if (viewingListingId) {
      setTimeout(this._scrollToViewingListing, 50);
    }
  }

  componentWillUnmount() {
    if (this.refreshHouseholdApplicationsInterval) {
      global.clearInterval(this.refreshHouseholdApplicationsInterval);
    }
  }

  _scrollToViewingListing = () => {
    const { viewingListingId } = this.props.locationQuery;
    const element = global.document.querySelector(
      `.SrRenterApplicationStatusCardSunroom-listing-${viewingListingId}`,
    );

    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  _refreshHouseholdApplications = () => {
    const { dispatch, currentUser } = this.props;
    dispatch(HouseholdApplicationActions.getHouseholdApplicationsByUserId, {
      userId: currentUser.id,
    });
  };

  _onSettingsClick = () => {
    this.setState({
      showSettingsModal: true,
    });
  };

  _onCloseSettingsModal = () => {
    this.setState({
      showSettingsModal: false,
    });
  };

  _onInviteRoommateClick = ({ listing, householdId }) => {
    const { currentUser, appUrl } = this.props;
    const roommateInviteLink = makeInviteRoommateLink({
      user: currentUser,
      listing,
      appUrl,
      householdId,
    });
    this.setState({
      roommateInviteLink,
      showInviteRoommateModal: true,
    });
  };

  _onCloseInviteRoommateModal = () => {
    this.setState({
      showInviteRoommateModal: false,
    });
  };

  render() {
    const { userHouseholdApplications, locationQuery, currentUser } = this.props;

    const { showSettingsModal, showInviteRoommateModal, roommateInviteLink } = this.state;

    const viewingListingId = locationQuery.viewingListingId
      ? parseInt(locationQuery.viewingListingId, 10)
      : null; // eslint-disable-line max-len
    const showHouseholdApplicationSection =
      userHouseholdApplications && userHouseholdApplications.length > 0;

    if (hasRoleLandlord(currentUser.roles)) {
      return <Redirect to="/owner" />;
    }

    if (locationQuery.utilitySetup) {
      const householdApp = getAppWithUtilityActivation(userHouseholdApplications);

      if (householdApp) {
        const { utilityActivation, utilityPartner } = householdApp;
        return (
          <Redirect to={`/utility-setup/${utilityPartner.slug}/${utilityActivation.shortId}`} />
        );
      }
    }

    return (
      <div className="page-profile">
        <Navbar rightButton={<span className="settingsGear" onClick={this._onSettingsClick} />} />
        <SrCompleteYourProfileBlock />
        {showHouseholdApplicationSection && (
          <div className="blockHeaderContainer">
            <div className="blockHeader">Your Applications</div>
            <SrLink to="/faq-application" className="howItWorksLink">
              Application FAQs
            </SrLink>
          </div>
        )}
        <SrBox width="100%">
          <SrText display="block" align="center" weight="bold" padding="0 0 10px">
            Rent Your Home in 6 Minutes:
            <SrLink to="https://youtu.be/PHTjb4XwNtw?si=yAULpHzrFfTjKwve">
              {' Watch Video'}
            </SrLink>{' '}
            📺
          </SrText>
        </SrBox>
        <SrApplicationProcessVideo />
        <SrBox height="20px" />

        {showHouseholdApplicationSection && (
          <div className="SrRenterApplicationStatusCardList">
            {userHouseholdApplications.map((app) => {
              const CardComponent = isSunroomListing(app.listing)
                ? SrRenterApplicationStatusCardSunroom
                : SrRenterApplicationStatusCard;

              return (
                <CardComponent
                  key={`srascl-${app.listing.id}-${app.householdListing.householdId}`}
                  listing={app.listing}
                  concession={app.concession}
                  partner={app.partner}
                  household={app.household}
                  moveInDate={app.moveInDate}
                  market={app.market}
                  householdListing={app.householdListing}
                  securityDepositStripeCharge={app.securityDepositStripeCharge}
                  securityDepositPaymentAccount={app.securityDepositPaymentAccount}
                  defaultPaymentAccount={app.defaultPaymentAccount}
                  householdUsers={app.householdUsers}
                  roommateApplications={app.roommateApplications}
                  guarantorApplications={app.guarantorApplications}
                  applicationSubmissions={app.applicationSubmissions}
                  householdApplicationsStatusUpdates={app.householdApplicationsStatusUpdates}
                  tenantScreeningCriteria={app.tenantScreeningCriteria}
                  tenantAlertReports={app.tenantAlertReports}
                  householdGuarantors={app.householdGuarantors}
                  renters={app.renters}
                  viewing={viewingListingId === app.listing.id}
                  lease={app.lease}
                  marketingPartnerListing={app.marketingPartnerListing}
                  onInviteRoommateClicked={this._onInviteRoommateClick}
                  utilityActivation={app.utilityActivation}
                  utilityPartner={app.utilityPartner}
                />
              );
            })}
          </div>
        )}
        {!showHouseholdApplicationSection && (
          <SrBox marginTop="20px">
            <SrBox margin="0 auto" width="min-content">
              <SrIcon className="house-illustration" />
            </SrBox>
            <SrBox marginTop="10px" padding="0 50px" textAlign="center">
              <SrText size="2xl" lineHeight="35px" weight="bold" color="grey600">
                Rental Applications will show up here
              </SrText>
            </SrBox>
            <SrBox marginTop="5px" padding="0 10px" textAlign="center">
              <SrText lineHeight="25px" color="grey600">
                You have not applied to any rental homes yet.
                <br />
                When you do apply, visit this page to see the status of your applications.
              </SrText>
            </SrBox>
          </SrBox>
        )}

        <TabBar />

        {showSettingsModal && (
          <SrSettingsModal isOpen={showSettingsModal} onClose={this._onCloseSettingsModal} />
        )}
        <SrInviteRoommateModal
          roommateInviteLink={roommateInviteLink}
          showModal={showInviteRoommateModal}
          onClose={this._onCloseInviteRoommateModal}
        />
      </div>
    );
  }
}

RenterProfilePage.displayName = 'RenterProfilePage';

export default RenterProfilePage;
